import React, {useState, useRef} from 'react';
import PropTypes from 'prop-types';
import {AnimatePresence} from 'framer-motion';
import {useSelector} from 'react-redux';

import IconChevronLeft from '@spothero/icons/chevron-left';
import IconSearch from '@spothero/icons/search';
import IconBuildings from '@spothero/icons/buildings';
import {Box, Icon, Text} from '@spothero/ui';

import {useAnimationContext} from 'common/animation/AnimationContext';
import MotionBox from 'common/animation/MotionBox';
import MotionCircle from 'common/animation/MotionCircle';
import {selectAllFilteredFacilities} from 'pages/facilities/store/facilities-selectors';

import FacilitySidebarHeader from './FacilitySidebarHeader';
import SearchWindow from './search-window/SearchWindow';
import Badge from 'common/badge/Badge';

const facilitySidebarContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    height: '100%',
    width: '21.875rem',
    overflow: 'hidden',
    backgroundColor: 'white',
    zIndex: 'layer3',
    borderRight: '1px solid',
    borderColor: 'gray.200',
};

const collapseToggleStyles = {
    position: 'fixed',
    top: '2.625rem',
    size: 7,
    backgroundColor: 'gray.light',
    zIndex: 'layer5',
    border: '1px solid',
    borderColor: 'gray.medium',
};

const sharedTransionProps = {
    type: 'spring',
    duration: 0.5,
    bounce: 0,
};

const collapseToggleAnimationVariants = {
    collapsed: {
        left: '3.5rem',
        rotate: 180,
        transition: {
            ...sharedTransionProps,
            delay: 0.125,
        },
    },
    expanded: {
        left: '21rem',
        rotate: 0,
        transition: {
            ...sharedTransionProps,
            delay: 0,
        },
    },
};

const facilitySidebarContainerAnimationVariants = {
    collapsed: {
        width: '4.375rem',
        transition: {
            ...sharedTransionProps,
            delay: 0.125,
        },
    },
    expanded: {
        width: '21.875rem',
        transition: {
            ...sharedTransionProps,
            delay: 0,
        },
    },
};

const facilitySidebarContentAnimationProps = {
    initial: {opacity: 0},
    animate: {
        opacity: 1,
        transition: {duration: 0.125, delay: 0.125},
    },
    exit: {
        opacity: 0,
        transition: {duration: 0.125, delay: 0},
    },
};

const sidebarCollapsedContentAnimationProps = {
    initial: {opacity: 0},
    animate: {
        opacity: 1,
        transition: {duration: 0.125, delay: 0.5},
    },
    exit: {
        opacity: 0,
        transition: {duration: 0.125, delay: 0},
    },
};

const badgeStyleProps = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: -3,
    right: 1.5,
    height: 5,
    width: 5,
    fontSize: 10,
    fontWeight: 'normal',
};

const FacilitySidebar = ({userData}) => {
    const containerRef = useRef(null);
    const filteredFacilities = useSelector(selectAllFilteredFacilities);
    const {
        collapsed: [collapsed, setCollapsed],
    } = useAnimationContext();
    const [areFiltersOpen, setAreFiltersOpen] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);
    const selectedCount = filteredFacilities.length;

    const handleCollapseToggle = () => {
        setCollapsed(!collapsed);
        setAreFiltersOpen(false);
    };

    const handleClickSearch = () => {
        setTabIndex(0);

        handleCollapseToggle();
    };

    const handleClickSelectedFacilities = () => {
        setTabIndex(1);

        handleCollapseToggle();
    };

    const onFiltersToggle = () => setAreFiltersOpen((prevState) => !prevState);

    return (
        <>
            <MotionCircle
                as="button"
                onClick={handleCollapseToggle}
                {...collapseToggleStyles}
                variants={collapseToggleAnimationVariants}
                initial={false}
                animate={collapsed ? 'collapsed' : 'expanded'}
            >
                <Icon as={IconChevronLeft} boxSize={4} fill="gray.dark" />
            </MotionCircle>
            <MotionBox
                data-testid="FacilitySidebar-Container"
                zIndex="layer2"
                {...facilitySidebarContainerStyles}
                variants={facilitySidebarContainerAnimationVariants}
                initial={false}
                animate={collapsed ? 'collapsed' : 'expanded'}
            >
                <FacilitySidebarHeader
                    isOpen={!collapsed}
                    userEmail={userData?.email}
                />
                <AnimatePresence initial={false}>
                    {!collapsed && (
                        <MotionBox
                            ref={containerRef}
                            key="search-window"
                            {...facilitySidebarContentAnimationProps}
                        >
                            {/* All Content to Show/Hide when collapsing the Side bar goes here */}
                            {!collapsed && (
                                <SearchWindow
                                    areFiltersOpen={areFiltersOpen}
                                    onFiltersToggle={onFiltersToggle}
                                    containerRef={containerRef}
                                    tabIndex={tabIndex}
                                    setTabIndex={setTabIndex}
                                />
                            )}
                        </MotionBox>
                    )}
                    {collapsed && (
                        <MotionBox
                            key="collapsed-content"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            gap={8}
                            marginTop={8}
                            {...sidebarCollapsedContentAnimationProps}
                        >
                            <Box
                                as="button"
                                onClick={handleClickSearch}
                                maxWidth={14}
                                textAlign="center"
                            >
                                <Icon as={IconSearch} boxSize={6} />
                                <Text fontSize="xs">Search</Text>
                            </Box>
                            <Box
                                as="button"
                                onClick={handleClickSelectedFacilities}
                                maxWidth={14}
                                textAlign="center"
                                position="relative"
                            >
                                <Badge {...badgeStyleProps}>
                                    {selectedCount > 999
                                        ? '1k+'
                                        : selectedCount}
                                </Badge>
                                <Icon as={IconBuildings} boxSize={6} />
                                <Text fontSize="xs">Selected Facilities</Text>
                            </Box>
                        </MotionBox>
                    )}
                </AnimatePresence>
            </MotionBox>
        </>
    );
};

/* eslint-disable camelcase */
FacilitySidebar.propTypes = {
    isCollapsed: PropTypes.bool,
    setIsCollapsed: PropTypes.func,
    userData: PropTypes.shape({
        user_id: PropTypes.number,
        operator_id: PropTypes.number,
        email: PropTypes.string,
        can_view_inventory_management: PropTypes.bool,
        can_view_event_tiers: PropTypes.bool,
        can_dispute_refunds: PropTypes.bool,
        facility_count: PropTypes.number,
        email_cadence: PropTypes.string,
        is_staff: PropTypes.bool,
        date_joined: PropTypes.string,
    }),
};
/* eslint-enable camelcase */

export default FacilitySidebar;
